import { Fragment, useState } from 'react';
import { Icon, StateBox } from '@garnet/ui';
import type { LedgerEntry } from '../../data/types.js';
import { CitationChip, Confidence } from '../../components/advisor.js';

/* Garnet · AI Advisor — Recommendation ledger.
   Every recommendation and its human response (accepted / modified / rejected /
   pending), with who responded and when. This sits in the audit trail. */

type Response = LedgerEntry['response'];
type Filter = 'all' | Response;

const RESP: Record<Response, { c: string; t: string; bg: string; label: string }> = {
  accepted: { c: 'var(--verified)', t: 'var(--verified-ink)', bg: 'var(--verified-bg)', label: 'accepted' },
  modified: { c: 'var(--attest)', t: 'var(--attest-ink)', bg: 'var(--attest-bg)', label: 'modified' },
  rejected: { c: 'var(--neutral)', t: 'var(--neutral-ink)', bg: 'var(--neutral-bg)', label: 'rejected' },
  pending: { c: 'var(--adv)', t: 'var(--adv-ink)', bg: 'var(--adv-chip)', label: 'pending' },
};

function RespCell({ r }: { r: Response }) {
  const m = RESP[r];
  return (
    <span className="lg-resp" style={{ color: m.t }}>
      <span className="lg-dot" style={{ background: m.c }} />
      {m.label}
    </span>
  );
}

function LedgerRow({ row, open, onToggle }: { row: LedgerEntry; open: boolean; onToggle: () => void }) {
  return (
    <Fragment>
      <tr className={'lrow' + (open ? ' open' : '')} onClick={onToggle}>
        <td className="mono" style={{ whiteSpace: 'nowrap', color: 'var(--adv-ink)', fontWeight: 600 }}>
          {row.ref}
        </td>
        <td className="mono" style={{ whiteSpace: 'nowrap', color: 'var(--muted)' }}>
          {row.at}
        </td>
        <td className="mono" style={{ whiteSpace: 'nowrap' }}>
          {row.surface}
        </td>
        <td style={{ minWidth: 260 }}>
          <div style={{ fontSize: 13, color: 'var(--ink)', lineHeight: 1.4 }}>{row.rec}</div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)', marginTop: 3 }}>{row.supplier}</div>
        </td>
        <td>
          <RespCell r={row.response} />
        </td>
        <td className="mono" style={{ whiteSpace: 'nowrap', color: 'var(--muted)' }}>
          {row.by}
        </td>
        <td className="mono" style={{ whiteSpace: 'nowrap', color: 'var(--muted)' }}>
          {row.when}
        </td>
        <td style={{ width: 18, textAlign: 'right', color: 'var(--muted)' }}>
          <span style={{ display: 'inline-block', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}>
            <Icon name="chevron" size={14} />
          </span>
        </td>
      </tr>
      {open && (
        <tr className="ldrawer">
          <td colSpan={8}>
            <div className="ldrawer-in">
              <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '9px 16px', alignItems: 'start' }}>
                <span style={{ fontFamily: 'var(--mono)', fontSize: 10.5, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--muted)' }}>
                  grounding
                </span>
                <span>
                  <CitationChip c={row.cite} />
                </span>
                <span style={{ fontFamily: 'var(--mono)', fontSize: 10.5, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--muted)' }}>
                  confidence
                </span>
                <span>
                  <Confidence level={row.confidence} />
                </span>
                <span style={{ fontFamily: 'var(--mono)', fontSize: 10.5, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--muted)' }}>
                  response
                </span>
                <span style={{ fontSize: 13, color: 'var(--ink)', lineHeight: 1.5 }}>
                  {row.detail || 'Awaiting a human response. No action has been taken on the record.'}
                  {row.link && (
                    <span
                      style={{
                        fontFamily: 'var(--mono)',
                        fontSize: 11.5,
                        color: 'var(--adv)',
                        borderBottom: '1px solid var(--adv-line)',
                        marginLeft: 8,
                        cursor: 'pointer',
                      }}
                    >
                      {row.link} →
                    </span>
                  )}
                </span>
              </div>
            </div>
          </td>
        </tr>
      )}
    </Fragment>
  );
}

export function Ledger({ ledger }: { ledger: LedgerEntry[] }) {
  const [openRef, setOpenRef] = useState<string | null>('ADV-REC-4198');
  const [filter, setFilter] = useState<Filter>('all');

  const counts = ledger.reduce<Record<string, number>>((a, r) => {
    a[r.response] = (a[r.response] ?? 0) + 1;
    return a;
  }, {});
  const rows = ledger.filter((r) => filter === 'all' || r.response === filter);

  const chip = (k: Filter, label: string) => {
    const active = filter === k;
    return (
      <button
        key={k}
        onClick={() => setFilter(k)}
        aria-pressed={active}
        style={{
          fontFamily: 'var(--mono)',
          fontSize: 11,
          fontWeight: 600,
          padding: '6px 11px',
          borderRadius: 'var(--radius-md)',
          cursor: 'pointer',
          border: '1px solid ' + (active ? 'var(--ink)' : 'var(--rule)'),
          background: active ? 'var(--ink)' : 'var(--panel)',
          color: active ? 'var(--paper)' : 'var(--muted)',
        }}
      >
        {label}
        {k !== 'all' && counts[k] ? <span style={{ marginLeft: 6, opacity: 0.7 }}>{counts[k]}</span> : null}
      </button>
    );
  };

  return (
    <div className="adv-stage-inner wide ledger">
      <div className="adv-h">
        <div className="eb">§ Recommendation ledger · advisor</div>
        <h1>Every recommendation, and its answer</h1>
        <p>
          The full record of what the Advisor proposed and how a human responded — accepted, modified, rejected, or still
          pending — with who and when. It sits in the same audit trail as the verification record.
        </p>
      </div>

      <div style={{ display: 'flex', gap: 7, marginBottom: 14, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--muted)', marginRight: 2 }}>
          response
        </span>
        {chip('all', 'all')}
        {chip('accepted', 'accepted')}
        {chip('modified', 'modified')}
        {chip('rejected', 'rejected')}
        {chip('pending', 'pending')}
      </div>

      <div className="panel" style={{ overflowX: 'auto' }}>
        <table className="tbl" style={{ minWidth: 880 }}>
          <thead>
            <tr>
              <th>ref</th>
              <th>raised</th>
              <th>surface</th>
              <th>recommendation</th>
              <th>response</th>
              <th>by</th>
              <th>when</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={8}>
                  <StateBox kind="empty" title="No recommendations with this response" />
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <LedgerRow
                  key={r.ref}
                  row={r}
                  open={openRef === r.ref}
                  onToggle={() => setOpenRef((o) => (o === r.ref ? null : r.ref))}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      <div
        style={{
          fontFamily: 'var(--mono)',
          fontSize: 11,
          color: 'var(--muted)',
          marginTop: 12,
          display: 'flex',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 8,
        }}
      >
        <span>
          {rows.length} of {ledger.length} recommendations
        </span>
        <span>exported with the qualification record · click a row for the response detail</span>
      </div>
    </div>
  );
}
